import { IReportPeriodResponse } from '../services/report-period/report-period.model';

export interface ILabelValue {
  label: string | number;
  [key: string]: string | number | undefined;
}

export function aggregateTopNWithOther(
  items: ILabelValue[] | unknown,
  topN: number,
  valueKey: string,
): ILabelValue[] {
  const safeItems = Array.isArray(items) ? items : [];
  const sorted = safeItems
    .slice()
    .sort((leftItem, rightItem) => Number(rightItem[valueKey] || 0) - Number(leftItem[valueKey] || 0));
  const topResults = sorted.slice(0, topN);
  const otherTotal = sorted.slice(topN).reduce((sum, item) => sum + Number(item[valueKey] || 0), 0);
  if (otherTotal > 0 && sorted.length > topN) {
    topResults.push({ label: 'Other', [valueKey]: Number(otherTotal) } as ILabelValue);
  }
  return topResults.sort((leftItem, rightItem) =>
    String(leftItem.label).localeCompare(String(rightItem.label), undefined, { sensitivity: 'base' }),
  );
}

export type TopNValue = number | 'all';

/**
 * Client-side Top-N + "Other" slicing for charts, returning items DESCENDING by
 * value with "Other" appended last (so callers can take the true top N for a
 * compact legend). `topN === 'all'` (or count <= topN) returns all, no "Other".
 */
export function applyTopNWithOther(
  items: ILabelValue[] | unknown,
  topN: TopNValue,
  valueKey: string,
  /** Additional numeric keys (e.g. a comparison series) summed into the "Other" row too. */
  extraSumKeys: string[] = [],
): ILabelValue[] {
  const safeItems = Array.isArray(items) ? items : [];
  const sorted = safeItems
    .slice()
    .sort((leftItem, rightItem) => Number(rightItem[valueKey] || 0) - Number(leftItem[valueKey] || 0));
  if (topN === 'all' || sorted.length <= topN) return sorted;
  const top = sorted.slice(0, topN);
  const rest = sorted.slice(topN);
  const otherTotal = rest.reduce((sum, item) => sum + Number(item[valueKey] || 0), 0);
  const extraTotals = extraSumKeys.map((key) => rest.reduce((sum, item) => sum + Number(item[key] || 0), 0));
  if (otherTotal > 0 || extraTotals.some((total) => total > 0)) {
    const otherRow = { label: 'Other', [valueKey]: Number(otherTotal) } as ILabelValue;
    extraSumKeys.forEach((key, i) => { otherRow[key] = extraTotals[i]; });
    top.push(otherRow);
  }
  return top;
}

/**
 * Joins a current-period and a comparison-period dataset on `label`, producing rows carrying
 * both values (missing side = 0) so a chart can render them as two series. Rows keep the
 * current period's descending order; comparison-only rows follow, largest first.
 */
export function mergeComparisonByLabel(
  current: Array<{ label: string;[key: string]: any }> | undefined,
  comparison: Array<{ label: string;[key: string]: any }> | undefined,
  valueKey: string,
  comparisonKey: string,
): ILabelValue[] {
  const safeCurrent = Array.isArray(current) ? current : [];
  const safeComparison = Array.isArray(comparison) ? comparison : [];
  const comparisonByLabel = new Map(safeComparison.map((item) => [String(item.label), Number(item[valueKey] || 0)]));
  const rows: ILabelValue[] = safeCurrent
    .slice()
    .sort((a, b) => Number(b[valueKey] || 0) - Number(a[valueKey] || 0))
    .map((item) => ({
      // Carry the entity id through so a bar stays drillable when a comparison period is set.
      // Comparison-only rows below have no current-period id (they are 0 this period).
      ...(item.id != null ? { id: item.id } : {}),
      label: item.label,
      [valueKey]: Number(item[valueKey] || 0),
      [comparisonKey]: comparisonByLabel.get(String(item.label)) ?? 0,
    }));
  const currentLabels = new Set(safeCurrent.map((item) => String(item.label)));
  safeComparison
    .filter((item) => !currentLabels.has(String(item.label)))
    .sort((a, b) => Number(b[valueKey] || 0) - Number(a[valueKey] || 0))
    .forEach((item) => {
      rows.push({ label: item.label, [valueKey]: 0, [comparisonKey]: Number(item[valueKey] || 0) });
    });
  return rows;
}

export interface IStackedSeriesItem {
  dataKey: string;
  label: string;
  stack: string;
  id?: string;
  valueFormatter?: (value: any) => any;
  color?: string;
}

/**
 * Per-row totals of a stacked dataset (every numeric field that is not the axis key),
 * e.g. a monthly history's total across all entities for each month.
 */
export function stackedRowTotals(rows: Array<Record<string, any>> | unknown, axisKeys: string[] = ['month', 'displayIndex']): number[] {
  const safeRows = Array.isArray(rows) ? rows : [];
  return safeRows.map((row) => Object.entries(row).reduce((sum, [key, value]) =>
    (axisKeys.includes(key) || typeof value !== 'number' || !Number.isFinite(value)) ? sum : sum + value, 0));
}

type StackedRow = Record<string, any>;

/**
 * Top-N + "Other" for STACKED charts, where each SERIES is a category and each
 * ROW is an x-axis bucket (a month). `applyTopNWithOther` slices rows, so it
 * would drop months here.
 *
 * Ranks series by their total across every row, keeps the biggest `topN`, and
 * folds the rest into one "Other" series whose per-row value is the sum of the
 * dropped series for that row — so the bar totals are unchanged and only the
 * banding collapses. Series come back DESCENDING by total with "Other" last:
 * MUI stacks in series order (StackOrder.none), so the largest sits at the
 * bottom, and widening the selection only appends rather than re-shuffling the
 * colours already on screen.
 */
export function applyTopNToStackedSeries(
  dataset: StackedRow[] | unknown,
  series: IStackedSeriesItem[] | unknown,
  topN: TopNValue,
): { dataset: StackedRow[]; series: IStackedSeriesItem[] } {
  const safeRows = Array.isArray(dataset) ? (dataset as StackedRow[]) : [];
  const safeSeries = Array.isArray(series) ? (series as IStackedSeriesItem[]) : [];
  if (safeSeries.length === 0) return { dataset: safeRows, series: safeSeries };

  const totalOf = (item: IStackedSeriesItem) =>
    safeRows.reduce((sum, row) => sum + Number(row[item.dataKey] || 0), 0);

  const ranked = safeSeries
    .map((item) => ({ item, total: totalOf(item) }))
    .sort((leftItem, rightItem) => rightItem.total - leftItem.total);

  // Explicit ids keep a category's bars identified across a Top N change: MUI
  // otherwise falls back to `auto-generated-id-<index>` and keys its bar
  // transitions on that, morphing one category's bars into another's.
  const withIds = (entries: Array<{ item: IStackedSeriesItem }>): IStackedSeriesItem[] =>
    entries.map(({ item }) => ({ ...item, id: item.id ?? item.dataKey }));

  if (topN === 'all' || ranked.length <= topN) {
    return { dataset: safeRows, series: withIds(ranked) };
  }

  const kept = ranked.slice(0, topN);
  const dropped = ranked.slice(topN);
  const droppedTotal = dropped.reduce((sum, entry) => sum + entry.total, 0);
  if (droppedTotal <= 0) return { dataset: safeRows, series: withIds(kept) };

  // A real category can be named "Other", so the merged series takes a sentinel
  // dataKey and carries its human name in `label` only.
  const existingKeys = new Set(safeSeries.map((item) => item.dataKey));
  let otherKey = '__other__';
  let suffix = 2;
  while (existingKeys.has(otherKey)) otherKey = `__other__${suffix++}`;

  const droppedKeys = dropped.map((entry) => entry.item.dataKey);
  // Dropped keys stay on the row: MUI only reads keys a series names.
  const nextDataset = safeRows.map((row) => ({
    ...row,
    [otherKey]: droppedKeys.reduce((sum, key) => sum + Number(row[key] || 0), 0),
  }));

  const template = kept[0]?.item;
  const otherSeries: IStackedSeriesItem = {
    dataKey: otherKey,
    id: otherKey,
    label: `Other (${dropped.length} more)`,
    stack: template?.stack ?? 'total',
    valueFormatter: template?.valueFormatter,
  };

  return { dataset: nextDataset, series: [...withIds(kept), otherSeries] };
}

export function getPreviousReportPeriod(
  currentId: string | undefined,
  allReportPeriods: IReportPeriodResponse[],
): IReportPeriodResponse | undefined {
  if (!currentId || allReportPeriods.length < 2) return undefined;
  const current = allReportPeriods.find((p) => p.id === currentId);
  if (!current) return undefined;
  const currentStart = new Date(current.startDate).getTime();
  const candidates = allReportPeriods.filter(
    (p) => p.id !== currentId && new Date(p.endDate).getTime() < currentStart,
  );
  if (candidates.length === 0) return undefined;
  return candidates.reduce((prev, curr) =>
    new Date(curr.endDate).getTime() > new Date(prev.endDate).getTime() ? curr : prev,
  );
}

export function calcPctChange(
  currentValue: number,
  previousValue: number,
): number | undefined {
  if (!Number.isFinite(previousValue) || !Number.isFinite(currentValue)) return undefined;
  if (previousValue === 0) return undefined;
  return ((currentValue - previousValue) / previousValue) * 100;
}
